/**
 * Alpine.js Profile Store
 * Global state management for profile editing
 */

import Alpine from 'alpinejs';

// Initialize the profile store
Alpine.store('profile', {
    // User data
    user: {},
    
    // Track pending changes before saving
    pendingChanges: {},
    
    // Track field modification timestamps for conflict detection
    fieldTimestamps: {},
    
    // Track active editors for UI feedback
    activeEditors: new Set(),
    
    
    /**
     * Update a field value in the store
     */
    updateField(field, value) {
        if (!this.user) {
            this.user = {};
        }
        
        this.user[field] = value;
        this.fieldTimestamps[field] = Date.now();
        
        // Clear from pending changes once saved
        if (this.pendingChanges[field] !== undefined) {
            delete this.pendingChanges[field];
        }
    },
    
    /**
     * Track a pending change before saving
     */
    setPendingChange(field, value) {
        this.pendingChanges[field] = value;
        this.fieldTimestamps[field] = Date.now();
    },
    
    /**
     * Check if a field was recently modified (potential conflict)
     */
    isRecentlyModified(field, withinMs = 5000) {
        const timestamp = this.fieldTimestamps[field];
        if (!timestamp) return false;
        return (Date.now() - timestamp) < withinMs;
    },
    
    /**
     * Register an active editor
     */
    startEditing(fieldName) {
        this.activeEditors.add(fieldName);
    },
    
    /**
     * Unregister an active editor
     */
    stopEditing(fieldName) {
        this.activeEditors.delete(fieldName);
    },
    
    /**
     * Get count of active editors
     */
    get editingCount() {
        return this.activeEditors.size;
    },
    
    /**
     * Clear all pending changes
     */
    clearPendingChanges() {
        this.pendingChanges = {};
    },
    
    /**
     * Clean up old timestamps (older than 1 hour)
     */
    cleanupOldTimestamps() {
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        Object.keys(this.fieldTimestamps).forEach(field => {
            if (this.fieldTimestamps[field] < oneHourAgo) {
                delete this.fieldTimestamps[field];
            }
        });
    },
    
    /**
     * Get the current value of a field
     */
    getFieldValue(field) {
        // Check pending changes first
        if (this.pendingChanges[field] !== undefined) {
            return this.pendingChanges[field];
        }
        
        // Then check user data
        if (this.user && this.user[field] !== undefined) {
            return this.user[field];
        }
        
        return '';
    },
    
    /**
     * Initialize the store with user data
     */
    init(userData = {}) {
        this.user = userData;
        this.pendingChanges = {};
        this.fieldTimestamps = {};
        this.activeEditors = new Set();
        
        // Set up periodic cleanup of old timestamps
        if (!this._cleanupInterval) {
            this._cleanupInterval = setInterval(() => {
                this.cleanupOldTimestamps();
            }, 5 * 60 * 1000); // Every 5 minutes
        }
    }
});

// Export for use in other modules
export default Alpine.store('profile');